"use client";

import { Link } from "@/i18n/navigation";
import {
  PhoneIcon,
  ClipboardDocumentCheckIcon,
  CheckCircleIcon,
  CurrencyEuroIcon,
  ArrowRightIcon,
  GiftIcon,
  BuildingOffice2Icon,
  HomeModernIcon,
  ArchiveBoxIcon,
} from "@heroicons/react/24/outline";
import CtaLink from "@/components/ui/CtaLink";
import Contact from "@/components/Contact/Contact";

const PHONE_TEL = "+32493966507";
const PHONE_DISPLAY = "+32 (0)493 96 65 07";

// Icons aren't translatable, kept separate from CONTENT and zipped back by
// array index (same pattern as homepage PageClient.jsx).
const PROPERTY_TYPE_ICONS = [BuildingOffice2Icon, HomeModernIcon, ArchiveBoxIcon];
const PROPERTY_TYPE_HREFS = ["/debarras-appartement", "/debarras-maison", "/debarras-cave-grenier-garage"];

const CONTENT = {
  fr: {
    h1: "Prix d'un vide maison à Bruxelles : comment est calculé votre devis",
    heroText:
      "Il n'existe pas de tarif fixe ni de prix au m³ pour un vide maison : chaque bien est différent. Nous établissons un devis gratuit et personnalisé qui tient compte du volume réel à évacuer, de l'accès au bien et des objets de valeur susceptibles d'être rachetés sur place, ce qui peut réduire votre facture.",
    ctaQuote: "Demander un devis gratuit",
    whyNoFixed: {
      title: "Pourquoi il n'y a pas de tarif fixe à Bruxelles",
      p1: "Le prix d'un vide maison dépend de paramètres propres à chaque bien : un studio à vider n'a rien à voir avec une maison de maître avec cave, grenier et jardin. Annoncer un prix au m³ ou un forfait générique reviendrait à ignorer ces différences et à proposer un chiffre qui ne correspondrait pas à votre situation réelle.",
      p2: "C'est pourquoi nous prenons le temps de comprendre votre bien — par téléphone, par photos ou lors d'une visite sur place si nécessaire — avant d'établir un devis précis. Cette estimation reste gratuite et sans engagement, que vous acceptiez ou non la proposition.",
    },
    factorsSection: {
      title: "Les facteurs qui déterminent le prix de votre vide maison",
      text: "Voici les éléments que nous évaluons systématiquement pour établir un devis cohérent avec votre situation.",
    },
    factors: [
      { title: "Le volume total à évacuer", text: "Un studio et une maison de maître avec cave et grenier n'impliquent ni le même temps de travail, ni le même nombre de personnes sur place." },
      { title: "L'accès au bien", text: "Étages sans ascenseur, escalier étroit, rue difficile à stationner : chaque contrainte d'accès allonge le temps d'intervention." },
      { title: "Le nombre de pièces et de dépendances", text: "Cave, grenier, garage, jardin ou remise ajoutent du volume et parfois des conditions de travail plus complexes (poussière, encombrement, accès restreint)." },
      { title: "La présence d'objets valorisables", text: "Meubles anciens, bijoux, argenterie ou objets de collection sont rachetés et leur valeur est déduite du prix du débarras." },
      { title: "L'urgence de l'intervention", text: "Une date de vente, un déménagement en maison de repos ou une échéance de bail imposent parfois un délai serré, à organiser en amont." },
      { title: "Le tri ou le nettoyage complémentaire", text: "Un tri fin pièce par pièce, la recherche de documents ou un nettoyage après évacuation demandent plus de temps qu'un débarras standard." },
    ],
    volumeAccess: {
      title: "Volume et accès : les deux postes qui pèsent le plus",
      p1: "Dans la grande majorité des devis, le volume à évacuer et l'accessibilité du bien sont les deux éléments qui influencent le plus le temps de travail — et donc le prix. Un rez-de-chaussée avec accès direct pour un camion se traite bien plus vite qu'un quatrième étage sans ascenseur dans une rue étroite du centre de Bruxelles, où le portage se fait entièrement à pied.",
      p2: "Les dépendances (cave, grenier, garage) comptent aussi dans le volume global, même lorsqu'elles paraissent secondaires : un grenier encombré depuis des décennies peut représenter autant de travail qu'une pièce à vivre entière. Nous demandons donc systématiquement une description de l'ensemble du bien, pas uniquement des pièces principales, pour éviter les mauvaises surprises entre l'estimation et le devis définitif.",
    },
    rachatSection: {
      badge: "Rachat d'objets de valeur",
      title: "Le rachat d'objets peut réduire votre facture",
      text: "Lorsque des objets de valeur sont trouvés sur place — meubles anciens, bijoux, argenterie, œuvres d'art, collections — nous les rachetons et déduisons leur valeur du prix du débarras. Un vide maison peut ainsi coûter sensiblement moins cher qu'attendu, en fonction de ce qui est découvert lors du tri.",
      link: "En savoir plus sur le rachat d'objets",
    },
    propertyTypeSection: {
      title: "Le prix varie aussi selon le type de bien",
      text: "Un vide maison complet, un débarras d'appartement et une intervention limitée à une cave ou un grenier ne suivent pas la même logique de prix, car le volume et la complexité diffèrent.",
      outroBefore: "Envie de voir toutes les communes où nous intervenons ? Consultez notre ",
      outroLink: "page vide maison Bruxelles",
      outroAfter: ".",
    },
    propertyTypes: [
      { title: "Appartement", text: "Le prix dépend surtout de la surface, de l'étage et de la présence d'un ascenseur.", label: "Débarras appartement" },
      { title: "Maison", text: "Plusieurs niveaux à traiter, souvent avec jardin, garage et dépendances : le volume est généralement plus important.", label: "Débarras maison" },
      { title: "Cave, grenier ou garage seul", text: "Une intervention ciblée sur un seul espace encombré, souvent plus rapide et moins coûteuse qu'un vide maison complet.", label: "Débarras cave, grenier, garage" },
    ],
    urgenceSection: {
      title: "Urgence et organisation : succession, vente, déménagement",
      p1: "Beaucoup de demandes de devis s'inscrivent dans un calendrier contraint : une succession à régler, une vente déjà signée, un départ en maison de repos ou la fin d'un bail. Ce degré d'urgence peut influencer l'organisation de l'intervention — nombre de personnes mobilisées, planification en une ou plusieurs journées — et donc le devis.",
      p2Before: "Plus vous nous transmettez le contexte tôt (date butoir, présence d'un notaire ou d'un syndic, accès aux clés), plus l'estimation peut être affinée rapidement. Pour un contexte de succession, notre article sur ",
      p2Link: "le vide maison dans le cadre d'une succession",
      p2After: " détaille les démarches à anticiper.",
    },
    processusSection: {
      title: "Comment obtenir votre devis",
      p1: "Contactez-nous par téléphone ou via le formulaire ci-dessous. Décrivez votre situation (type de bien, volume approximatif, accès, urgence) et joignez des photos si possible : cela nous permet de vous donner une première estimation rapidement. Pour les biens plus volumineux ou complexes, une visite sur place permet de confirmer le devis définitif avant toute intervention.",
      p2Before: "Cette estimation reste gratuite et sans engagement. Vous pouvez consulter des exemples d'interventions passées sur notre page ",
      p2Link1: "réalisations",
      p2Mid: " et l'avis de clients sur la page ",
      p2Link2: "avis",
      p2After: " avant de nous contacter.",
    },
    dechetsSection: {
      title: "Tri, évacuation et déchets : ce que couvre le devis",
      p1: "Un vide maison ne se limite pas à sortir des meubles : il inclut le tri sur place (objets à conserver, à racheter ou à évacuer), le chargement et le transport, ainsi que l'orientation des déchets vers les filières adaptées — recyclage, don ou destruction selon leur nature. Ces étapes font partie du volume de travail pris en compte dans le devis, au même titre que l'évacuation elle-même.",
      p2: "Si vous souhaitez conserver certains objets ou documents avant notre passage, il est préférable de les mettre de côté à l'avance : cela évite toute confusion pendant le tri et permet de concentrer le devis sur ce qui doit réellement être évacué.",
    },
    preparationSection: {
      title: "Comment préparer votre bien pour une estimation plus précise",
      p1: "Quelques informations simples suffisent souvent à affiner le devis dès le premier contact : le type de bien, une estimation du nombre de pièces à vider, la présence d'un étage ou d'un ascenseur, et si des objets méritent une attention particulière (meubles anciens, bijoux, collections). Des photos, même prises rapidement avec un téléphone, aident considérablement à évaluer le volume réel.",
      p2Before: "Pour une check-list plus complète des points à vérifier avant l'intervention, notre article ",
      p2Link: "checklist avant un vide maison",
      p2After: " détaille les démarches à anticiper (accès aux clés, syndic, objets à conserver, contacts utiles).",
    },
    transparenceSection: {
      title: "Un devis transparent, sans surprise",
      p1: "Le montant que nous vous communiquons avant l'intervention est celui qui sera facturé, sauf découverte majeure qui modifierait réellement le volume ou l'accès prévus initialement — un cas rare que nous signalons toujours avant d'agir. Il n'y a pas de frais cachés ajoutés en cours de chantier.",
      p2: "Nous privilégions un devis personnalisé plutôt qu'un forfait affiché, précisément parce qu'un chiffre générique ne pourrait pas refléter la diversité des biens à Bruxelles : appartement en étage, maison avec dépendances, cave ou grenier isolé. Cette approche prend un peu plus de temps qu'un tarif standard, mais elle évite les écarts entre l'estimation et le prix final.",
    },
    faqSectionTitle: "Questions fréquentes sur le prix d'un vide maison",
    faq: [
      { q: "Existe-t-il un tarif fixe ou un prix au m³ pour un vide maison à Bruxelles ?", a: "Non. Afficher un prix au m³ ou un forfait générique serait trompeur, car le volume réel, l'accès au bien et la présence d'objets valorisables font varier le prix d'une situation à l'autre. Nous préférons établir un devis précis après avoir compris votre situation plutôt que d'annoncer un chiffre qui ne correspondrait pas à la réalité de votre bien." },
      { q: "Comment est calculé le prix de mon devis ?", a: "Nous tenons compte du volume à évacuer, de l'accès (étages, ascenseur, stationnement), du nombre d'espaces à traiter (cave, grenier, garage) et de la présence éventuelle d'objets valorisables, dont la valeur est déduite. Plus les informations transmises sont précises, plus l'estimation initiale se rapproche du prix final." },
      { q: "Le devis est-il vraiment gratuit et sans engagement ?", a: "Oui. L'estimation ne vous coûte rien et ne vous engage à rien : vous restez libre d'accepter ou non la proposition. Nous établissons le devis sur base des informations transmises par téléphone, photos ou lors d'une visite sur place si le bien le nécessite." },
      { q: "Les objets de valeur trouvés sur place peuvent-ils réduire le prix ?", a: "Oui, c'est le principal levier pour alléger la facture. Meubles anciens, bijoux, argenterie, œuvres d'art ou objets de collection sont examinés et rachetés lorsqu'ils ont une valeur réelle, puis leur valeur est déduite du prix du débarras avant l'établissement du devis final." },
      { q: "L'accès au bien influence-t-il vraiment le prix ?", a: "Oui, et c'est souvent sous-estimé. Un quatrième étage sans ascenseur, une rue impossible à stationner pour un camion ou un escalier étroit rallongent nettement le temps de travail par rapport à un rez-de-chaussée avec accès direct. Cette contrainte est prise en compte dans le devis." },
      { q: "Dois-je être présent pour obtenir une estimation ?", a: "Pas nécessairement pour une première estimation : des photos et une description du bien (type, volume approximatif, accès) suffisent souvent à donner une fourchette de travail. Une visite sur place reste toutefois utile pour confirmer le devis définitif sur les biens plus volumineux ou complexes." },
      { q: "Le prix est-il le même pour un appartement, une maison ou une simple cave ?", a: "Non. Une intervention limitée à une cave, un grenier ou un garage est en général plus rapide et moins coûteuse qu'un vide maison complet, car le volume et le nombre d'espaces à traiter sont réduits. Un appartement en étage et une maison avec dépendances suivent chacun leur propre logique de prix." },
    ],
    ctaFinal: {
      title: "Prêt à connaître le prix de votre vide maison ?",
      text: "Décrivez-nous votre situation par téléphone ou via le formulaire : nous vous répondons avec une première estimation gratuite, sans engagement de votre part.",
    },
  },
  nl: {
    h1: "Prijs van een huis leegmaken in Brussel: hoe wordt uw offerte berekend",
    heroText:
      "Er bestaat geen vast tarief of prijs per m³ voor het leegmaken van een huis: elk pand is anders. Wij stellen een gratis en persoonlijke offerte op die rekening houdt met het werkelijke volume om af te voeren, de toegang tot het pand en de waardevolle voorwerpen die eventueel ter plaatse kunnen worden ingekocht, wat uw factuur kan verlagen.",
    ctaQuote: "Gratis offerte aanvragen",
    whyNoFixed: {
      title: "Waarom er geen vast tarief bestaat in Brussel",
      p1: "De prijs van een huis leegmaken hangt af van parameters die eigen zijn aan elk pand: een leeg te maken studio heeft niets te maken met een herenhuis met kelder, zolder en tuin. Een prijs per m³ of een generiek forfait aankondigen zou deze verschillen negeren en een cijfer voorstellen dat niet overeenstemt met uw werkelijke situatie.",
      p2: "Daarom nemen wij de tijd om uw pand te begrijpen — telefonisch, via foto's of, indien nodig, tijdens een bezoek ter plaatse — vóór wij een nauwkeurige offerte opstellen. Deze schatting blijft gratis en vrijblijvend, of u het voorstel al dan niet aanvaardt.",
    },
    factorsSection: {
      title: "De factoren die de prijs van uw huis leegmaken bepalen",
      text: "Dit zijn de elementen die wij systematisch beoordelen om een offerte op te stellen die overeenstemt met uw situatie.",
    },
    factors: [
      { title: "Het totale volume om af te voeren", text: "Een studio en een herenhuis met kelder en zolder vergen noch dezelfde werktijd, noch hetzelfde aantal medewerkers ter plaatse." },
      { title: "De toegang tot het pand", text: "Verdiepingen zonder lift, een smalle trap, een moeilijk te parkeren straat: elke toegangsbeperking verlengt de interventietijd." },
      { title: "Het aantal kamers en bijruimtes", text: "Kelder, zolder, garage, tuin of bergruimte verhogen het volume en soms ook de moeilijkheidsgraad van het werk (stof, rommel, beperkte toegang)." },
      { title: "De aanwezigheid van waardevolle voorwerpen", text: "Antieke meubelen, juwelen, zilverwerk of verzamelobjecten worden ingekocht en hun waarde wordt afgetrokken van de prijs van de ontruiming." },
      { title: "De urgentie van de interventie", text: "Een verkoopdatum, een verhuizing naar een rusthuis of het einde van een huurcontract leggen soms een krappe termijn op, die vooraf georganiseerd moet worden." },
      { title: "De bijkomende sortering of reiniging", text: "Een grondige sortering kamer per kamer, het opzoeken van documenten of een reiniging na de afvoer vergen meer tijd dan een standaardontruiming." },
    ],
    volumeAccess: {
      title: "Volume en toegang: de twee zwaarst wegende factoren",
      p1: "In de grote meerderheid van de offertes zijn het volume om af te voeren en de toegankelijkheid van het pand de twee elementen die de werktijd — en dus de prijs — het meest beïnvloeden. Een gelijkvloers met directe toegang voor een vrachtwagen wordt veel sneller behandeld dan een vierde verdieping zonder lift in een smalle straat in het centrum van Brussel, waar alles volledig te voet moet worden gedragen.",
      p2: "Bijruimtes (kelder, zolder, garage) tellen ook mee in het totale volume, zelfs wanneer ze secundair lijken: een zolder die al decennia vol staat, kan evenveel werk vergen als een volledige leefruimte. Wij vragen daarom systematisch een beschrijving van het volledige pand, niet enkel van de belangrijkste kamers, om verrassingen tussen de schatting en de definitieve offerte te vermijden.",
    },
    rachatSection: {
      badge: "Inkoop van waardevolle voorwerpen",
      title: "De inkoop van voorwerpen kan uw factuur verlagen",
      text: "Wanneer waardevolle voorwerpen ter plaatse worden gevonden — antieke meubelen, juwelen, zilverwerk, kunstwerken, verzamelingen — kopen wij ze in en trekken wij hun waarde af van de prijs van de ontruiming. Een huis leegmaken kan zo aanzienlijk minder kosten dan verwacht, afhankelijk van wat er tijdens de sortering wordt ontdekt.",
      link: "Meer over de inkoop van voorwerpen",
    },
    propertyTypeSection: {
      title: "De prijs varieert ook naargelang het type pand",
      text: "Een volledig huis leegmaken, het ontruimen van een appartement en een interventie beperkt tot een kelder of zolder volgen niet dezelfde prijslogica, want het volume en de complexiteit verschillen.",
      outroBefore: "Wilt u alle gemeenten zien waar wij actief zijn? Bekijk onze ",
      outroLink: "pagina huis leegmaken Brussel",
      outroAfter: ".",
    },
    propertyTypes: [
      { title: "Appartement", text: "De prijs hangt vooral af van de oppervlakte, de verdieping en de aanwezigheid van een lift.", label: "Appartement ontruimen" },
      { title: "Huis", text: "Meerdere niveaus te behandelen, vaak met tuin, garage en bijruimtes: het volume is doorgaans groter.", label: "Huis ontruimen" },
      { title: "Enkel kelder, zolder of garage", text: "Een gerichte interventie op één enkele volgestouwde ruimte, vaak sneller en goedkoper dan een volledig huis leegmaken.", label: "Kelder, zolder, garage ontruimen" },
    ],
    urgenceSection: {
      title: "Urgentie en organisatie: nalatenschap, verkoop, verhuizing",
      p1: "Veel offerteaanvragen passen binnen een strakke tijdslijn: een nalatenschap die geregeld moet worden, een reeds ondertekende verkoop, een vertrek naar een rusthuis of het einde van een huurcontract. Deze mate van urgentie kan de organisatie van de interventie beïnvloeden — aantal ingezette medewerkers, planning over één of meerdere dagen — en dus ook de offerte.",
      p2Before: "Hoe eerder u ons de context bezorgt (streefdatum, aanwezigheid van een notaris of syndicus, toegang tot de sleutels), hoe sneller de schatting verfijnd kan worden. Voor een context van nalatenschap behandelt ons artikel over ",
      p2Link: "huis leegmaken in het kader van een nalatenschap",
      p2After: " de stappen die u vooraf moet plannen.",
    },
    processusSection: {
      title: "Hoe u uw offerte krijgt",
      p1: "Neem contact met ons op per telefoon of via onderstaand formulier. Beschrijf uw situatie (type pand, geschat volume, toegang, urgentie) en voeg indien mogelijk foto's toe: zo kunnen wij u snel een eerste schatting geven. Voor grotere of complexere panden bevestigt een bezoek ter plaatse de definitieve offerte vóór elke interventie.",
      p2Before: "Deze schatting blijft gratis en vrijblijvend. U kunt voorbeelden van eerdere interventies bekijken op onze pagina ",
      p2Link1: "realisaties",
      p2Mid: " en de mening van klanten op de pagina ",
      p2Link2: "beoordelingen",
      p2After: " vóór u ons contacteert.",
    },
    dechetsSection: {
      title: "Sortering, afvoer en afval: wat de offerte dekt",
      p1: "Een huis leegmaken beperkt zich niet tot het buiten zetten van meubels: het omvat de sortering ter plaatse (voorwerpen om te bewaren, in te kopen of af te voeren), het laden en vervoeren, en het oriënteren van het afval naar de geschikte kanalen — recyclage, schenking of vernietiging naargelang de aard ervan. Deze stappen maken deel uit van de werklast die in de offerte wordt meegerekend, net als de afvoer zelf.",
      p2: "Als u bepaalde voorwerpen of documenten vóór ons bezoek wenst te bewaren, is het beter deze vooraf apart te zetten: dit voorkomt verwarring tijdens de sortering en laat toe de offerte te richten op wat werkelijk moet worden afgevoerd.",
    },
    preparationSection: {
      title: "Hoe u uw pand voorbereidt voor een nauwkeurigere schatting",
      p1: "Enkele eenvoudige gegevens volstaan vaak om de offerte al bij het eerste contact te verfijnen: het type pand, een schatting van het aantal te ledigen kamers, de aanwezigheid van een verdieping of lift, en of bepaalde voorwerpen bijzondere aandacht verdienen (antieke meubelen, juwelen, verzamelingen). Foto's, zelfs snel genomen met een telefoon, helpen aanzienlijk om het werkelijke volume in te schatten.",
      p2Before: "Voor een volledigere checklist van de punten die u vóór de interventie moet controleren, behandelt ons artikel ",
      p2Link: "checklist vóór het leegmaken van een huis",
      p2After: " de stappen die u vooraf moet plannen (toegang tot de sleutels, syndicus, te bewaren voorwerpen, nuttige contacten).",
    },
    transparenceSection: {
      title: "Een transparante offerte, zonder verrassingen",
      p1: "Het bedrag dat wij u vóór de interventie meedelen, is het bedrag dat gefactureerd zal worden, behalve bij een belangrijke vaststelling die het initieel voorziene volume of de toegang werkelijk zou wijzigen — een zeldzaam geval dat wij altijd melden vóór wij handelen. Er zijn geen verborgen kosten die tijdens de werf worden toegevoegd.",
      p2: "Wij geven de voorkeur aan een offerte op maat boven een vast forfait, net omdat een generiek cijfer de diversiteit van panden in Brussel niet zou kunnen weerspiegelen: appartement op verdieping, huis met bijruimtes, alleenstaande kelder of zolder. Deze aanpak vergt iets meer tijd dan een standaardtarief, maar vermijdt afwijkingen tussen de schatting en de eindprijs.",
    },
    faqSectionTitle: "Veelgestelde vragen over de prijs van een huis leegmaken",
    faq: [
      { q: "Bestaat er een vast tarief of een prijs per m³ voor een huis leegmaken in Brussel?", a: "Nee. Een prijs per m³ of een generiek forfait tonen zou misleidend zijn, want het werkelijke volume, de toegang tot het pand en de aanwezigheid van waardevolle voorwerpen doen de prijs van situatie tot situatie verschillen. Wij stellen liever een nauwkeurige offerte op nadat wij uw situatie begrepen hebben, dan een cijfer aan te kondigen dat niet overeenstemt met de realiteit van uw pand." },
      { q: "Hoe wordt de prijs van mijn offerte berekend?", a: "Wij houden rekening met het volume om af te voeren, de toegang (verdiepingen, lift, parkeren), het aantal te behandelen ruimtes (kelder, zolder, garage) en de eventuele aanwezigheid van waardevolle voorwerpen, waarvan de waarde wordt afgetrokken. Hoe nauwkeuriger de doorgegeven informatie, hoe dichter de eerste schatting bij de eindprijs ligt." },
      { q: "Is de offerte echt gratis en vrijblijvend?", a: "Ja. De schatting kost u niets en verplicht u tot niets: u blijft vrij om het voorstel al dan niet te aanvaarden. Wij stellen de offerte op basis van de informatie die telefonisch, via foto's of tijdens een bezoek ter plaatse (indien nodig) wordt doorgegeven." },
      { q: "Kunnen waardevolle voorwerpen die ter plaatse worden gevonden de prijs verlagen?", a: "Ja, dat is de belangrijkste hefboom om de factuur te verlichten. Antieke meubelen, juwelen, zilverwerk, kunstwerken of verzamelobjecten worden onderzocht en ingekocht wanneer ze een reële waarde hebben, waarna hun waarde wordt afgetrokken van de prijs van de ontruiming vóór het opstellen van de definitieve offerte." },
      { q: "Heeft de toegang tot het pand echt invloed op de prijs?", a: "Ja, en dit wordt vaak onderschat. Een vierde verdieping zonder lift, een straat waar een vrachtwagen onmogelijk kan parkeren of een smalle trap verlengen de werktijd aanzienlijk in vergelijking met een gelijkvloers met directe toegang. Deze beperking wordt meegenomen in de offerte." },
      { q: "Moet ik aanwezig zijn om een schatting te krijgen?", a: "Niet noodzakelijk voor een eerste schatting: foto's en een beschrijving van het pand (type, geschat volume, toegang) volstaan vaak om een werkvork te geven. Een bezoek ter plaatse blijft echter nuttig om de definitieve offerte te bevestigen bij grotere of complexere panden." },
      { q: "Is de prijs dezelfde voor een appartement, een huis of een eenvoudige kelder?", a: "Nee. Een interventie beperkt tot een kelder, zolder of garage is doorgaans sneller en goedkoper dan een volledig huis leegmaken, omdat het volume en het aantal te behandelen ruimtes kleiner zijn. Een appartement op verdieping en een huis met bijruimtes volgen elk hun eigen prijslogica." },
    ],
    ctaFinal: {
      title: "Klaar om de prijs van uw huis leegmaken te kennen?",
      text: "Beschrijf ons uw situatie telefonisch of via het formulier: wij antwoorden u met een eerste gratis schatting, zonder enige verplichting van uw kant.",
    },
  },
  en: {
    h1: "House clearance prices in Brussels: how your quote is calculated",
    heroText:
      "There is no fixed rate or price per m³ for a house clearance: every property is different. We draw up a free, personalised quote that takes into account the actual volume to be removed, access to the property, and any valuable items that could be bought back on site, which can reduce your bill.",
    ctaQuote: "Request a free quote",
    whyNoFixed: {
      title: "Why there is no fixed rate in Brussels",
      p1: "The price of a house clearance depends on factors specific to each property: a studio to be cleared has nothing in common with a townhouse with a cellar, attic and garden. Advertising a price per m³ or a generic flat rate would ignore these differences and offer a figure that would not match your actual situation.",
      p2: "That is why we take the time to understand your property — by phone, from photos, or during an on-site visit if needed — before drawing up an accurate quote. This estimate remains free and with no obligation, whether or not you accept the proposal.",
    },
    factorsSection: {
      title: "The factors that determine the price of your house clearance",
      text: "Here are the elements we systematically assess to produce a quote consistent with your situation.",
    },
    factors: [
      { title: "The total volume to remove", text: "A studio and a townhouse with a cellar and attic require neither the same working time nor the same number of people on site." },
      { title: "Access to the property", text: "Floors without a lift, a narrow staircase, a street that is hard to park on: every access constraint extends the intervention time." },
      { title: "The number of rooms and outbuildings", text: "A cellar, attic, garage, garden or shed add volume and sometimes more complex working conditions (dust, clutter, restricted access)." },
      { title: "The presence of valuable items", text: "Antique furniture, jewellery, silverware or collectible items are bought back and their value is deducted from the clearance price." },
      { title: "The urgency of the intervention", text: "A sale date, a move into a retirement home or a lease deadline sometimes impose a tight timeframe that needs to be organised in advance." },
      { title: "Additional sorting or cleaning", text: "A thorough room-by-room sort, searching for documents, or a clean after removal all take more time than a standard clearance." },
    ],
    volumeAccess: {
      title: "Volume and access: the two factors that weigh the most",
      p1: "In the vast majority of quotes, the volume to be removed and the accessibility of the property are the two elements that most influence working time — and therefore the price. A ground floor with direct access for a truck is handled far more quickly than a fourth floor without a lift on a narrow street in central Brussels, where everything has to be carried entirely on foot.",
      p2: "Outbuildings (cellar, attic, garage) also count towards the overall volume, even when they seem secondary: an attic cluttered for decades can represent as much work as an entire living room. We therefore systematically ask for a description of the whole property, not just the main rooms, to avoid unpleasant surprises between the estimate and the final quote.",
    },
    rachatSection: {
      badge: "Buyback of valuable items",
      title: "Item buyback can reduce your bill",
      text: "When valuable items are found on site — antique furniture, jewellery, silverware, artworks, collections — we buy them back and deduct their value from the clearance price. A house clearance can therefore end up costing noticeably less than expected, depending on what is discovered during sorting.",
      link: "Learn more about item buyback",
    },
    propertyTypeSection: {
      title: "The price also varies depending on the type of property",
      text: "A full house clearance, an apartment clearance and a job limited to a cellar or attic do not follow the same pricing logic, as the volume and complexity differ.",
      outroBefore: "Want to see every commune we cover? Check our ",
      outroLink: "Brussels house clearance page",
      outroAfter: ".",
    },
    propertyTypes: [
      { title: "Apartment", text: "The price depends mainly on the floor area, the floor level and whether there is a lift.", label: "Apartment clearance" },
      { title: "House", text: "Several levels to handle, often with a garden, garage and outbuildings: the volume is generally larger.", label: "House clearance" },
      { title: "Cellar, attic or garage only", text: "A targeted job on a single cluttered space, usually faster and cheaper than a full house clearance.", label: "Cellar, attic, garage clearance" },
    ],
    urgenceSection: {
      title: "Urgency and organisation: estates, sales, moves",
      p1: "Many quote requests fit into a tight timeline: an estate to settle, a sale already signed, a move into a retirement home or the end of a lease. This degree of urgency can influence how the intervention is organised — how many people are mobilised, whether it is planned over one or several days — and therefore the quote.",
      p2Before: "The earlier you share the context with us (deadline, involvement of a notary or property manager, access to keys), the faster the estimate can be refined. For an estate context, our article on ",
      p2Link: "house clearance as part of an estate in Brussels",
      p2After: " details the steps to plan ahead.",
    },
    processusSection: {
      title: "How to get your quote",
      p1: "Contact us by phone or via the form below. Describe your situation (type of property, approximate volume, access, urgency) and attach photos if possible: this lets us give you a first estimate quickly. For larger or more complex properties, an on-site visit confirms the final quote before any intervention.",
      p2Before: "This estimate remains free and with no obligation. You can see examples of past interventions on our ",
      p2Link1: "our work",
      p2Mid: " page and customer feedback on the ",
      p2Link2: "reviews",
      p2After: " page before contacting us.",
    },
    dechetsSection: {
      title: "Sorting, removal and waste: what the quote covers",
      p1: "A house clearance is not limited to carrying out furniture: it includes on-site sorting (items to keep, buy back or remove), loading and transport, as well as directing waste to the appropriate channels — recycling, donation or disposal depending on its nature. These steps are part of the workload taken into account in the quote, just like the removal itself.",
      p2: "If you wish to keep certain items or documents before our visit, it is best to set them aside in advance: this avoids any confusion during sorting and allows the quote to focus on what actually needs to be removed.",
    },
    preparationSection: {
      title: "How to prepare your property for a more accurate estimate",
      p1: "A few simple details are often enough to refine the quote from the first contact: the type of property, an estimate of the number of rooms to clear, whether there is a floor level or lift, and whether any items deserve special attention (antique furniture, jewellery, collections). Photos, even taken quickly with a phone, help considerably in assessing the actual volume.",
      p2Before: "For a more complete checklist of points to check before the intervention, our article ",
      p2Link: "checklist before a house clearance",
      p2After: " details the steps to plan ahead (access to keys, property manager, items to keep, useful contacts).",
    },
    transparenceSection: {
      title: "A transparent quote, with no surprises",
      p1: "The amount we give you before the intervention is the amount that will be invoiced, except in the case of a major discovery that would genuinely change the volume or access initially planned — a rare case that we always flag before acting. There are no hidden costs added during the job.",
      p2: "We favour a personalised quote over a published flat rate, precisely because a generic figure could not reflect the diversity of properties in Brussels: an apartment on a floor, a house with outbuildings, or a standalone cellar or attic. This approach takes a little more time than a standard rate, but it avoids discrepancies between the estimate and the final price.",
    },
    faqSectionTitle: "Frequently asked questions about house clearance prices",
    faq: [
      { q: "Is there a fixed rate or a price per m³ for a house clearance in Brussels?", a: "No. Showing a price per m³ or a generic flat rate would be misleading, as the actual volume, access to the property and the presence of valuable items make the price vary from one situation to another. We prefer to draw up an accurate quote after understanding your situation rather than announcing a figure that would not match the reality of your property." },
      { q: "How is the price of my quote calculated?", a: "We take into account the volume to be removed, access (floors, lift, parking), the number of spaces to handle (cellar, attic, garage) and any valuable items present, whose value is deducted. The more accurate the information provided, the closer the initial estimate is to the final price." },
      { q: "Is the quote really free and with no obligation?", a: "Yes. The estimate costs you nothing and commits you to nothing: you remain free to accept the proposal or not. We draw up the quote based on information provided by phone, photos, or an on-site visit if the property requires it." },
      { q: "Can valuable items found on site reduce the price?", a: "Yes, this is the main lever for lowering the bill. Antique furniture, jewellery, silverware, artworks or collectible items are examined and bought back when they have real value, and their value is then deducted from the clearance price before the final quote is drawn up." },
      { q: "Does access to the property really affect the price?", a: "Yes, and it is often underestimated. A fourth floor without a lift, a street where a truck cannot park, or a narrow staircase noticeably extend the working time compared with a ground floor with direct access. This constraint is factored into the quote." },
      { q: "Do I need to be present to get an estimate?", a: "Not necessarily for a first estimate: photos and a description of the property (type, approximate volume, access) are often enough to give a working range. An on-site visit remains useful, however, to confirm the final quote for larger or more complex properties." },
      { q: "Is the price the same for an apartment, a house or a simple cellar?", a: "No. A job limited to a cellar, attic or garage is generally faster and cheaper than a full house clearance, as the volume and number of spaces to handle are smaller. An apartment on a floor and a house with outbuildings each follow their own pricing logic." },
    ],
    ctaFinal: {
      title: "Ready to find out the price of your house clearance?",
      text: "Describe your situation to us by phone or via the form: we will get back to you with a first free estimate, with no obligation on your part.",
    },
  },
};

export default function PageClient({ locale }) {
  const c = CONTENT[locale];

  const faqStructuredData = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: c.faq.map((item) => ({
      "@type": "Question",
      name: item.q,
      acceptedAnswer: { "@type": "Answer", text: item.a },
    })),
  };

  return (
    <div>
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(faqStructuredData) }}
      />

      {/* HERO */}
      <section className="relative overflow-hidden bg-sand text-ink">
        <div
          className="pointer-events-none absolute inset-0"
          style={{
            background: "radial-gradient(900px 480px at 85% -10%, rgba(27,75,51,0.06), transparent 55%)",
          }}
        />
        <div className="relative mx-auto max-w-[1000px] px-6 py-20 text-center lg:px-10 lg:py-28">
          <CurrencyEuroIcon className="mx-auto h-10 w-10 text-accent" />
          <h1 className="mt-5 font-serif text-4xl font-black md:text-5xl">
            {c.h1}
          </h1>
          <p className="mx-auto mt-6 max-w-3xl text-lg text-ink/70 leading-relaxed">
            {c.heroText}
          </p>
          <div className="mt-9 flex flex-wrap justify-center gap-4">
            <CtaLink href={`tel:${PHONE_TEL}`} variant="accent" icon={PhoneIcon}>
              {PHONE_DISPLAY}
            </CtaLink>
            <CtaLink href="#contactus" variant="sand" icon={ClipboardDocumentCheckIcon}>
              {c.ctaQuote}
            </CtaLink>
          </div>
        </div>
      </section>

      {/* POURQUOI PAS DE TARIF FIXE */}
      <section className="bg-white py-20">
        <div className="mx-auto max-w-[1000px] px-6 lg:px-10">
          <h2 className="font-serif text-2xl font-extrabold text-brand-900 md:text-3xl">
            {c.whyNoFixed.title}
          </h2>
          <p className="mt-4 leading-relaxed text-neutral-700">
            {c.whyNoFixed.p1}
          </p>
          <p className="mt-4 leading-relaxed text-neutral-700">
            {c.whyNoFixed.p2}
          </p>
        </div>
      </section>

      {/* FACTEURS */}
      <section className="bg-sand py-20">
        <div className="mx-auto max-w-[1000px] px-6 lg:px-10">
          <h2 className="font-serif text-2xl font-extrabold text-brand-900 md:text-3xl">
            {c.factorsSection.title}
          </h2>
          <p className="mt-4 max-w-2xl leading-relaxed text-neutral-700">
            {c.factorsSection.text}
          </p>
          <div className="mt-8 grid grid-cols-1 gap-4 sm:grid-cols-2">
            {c.factors.map((factor) => (
              <div key={factor.title} className="flex items-start gap-3 rounded-2xl border border-sand-line bg-white p-5">
                <CheckCircleIcon className="mt-0.5 h-5 w-5 shrink-0 text-accent" />
                <div>
                  <p className="text-sm font-bold text-ink">{factor.title}</p>
                  <p className="mt-1 text-sm leading-relaxed text-neutral-600">{factor.text}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* VOLUME / ACCES */}
      <section className="bg-white py-20">
        <div className="mx-auto max-w-[1000px] px-6 lg:px-10">
          <h2 className="font-serif text-2xl font-extrabold text-brand-900 md:text-3xl">
            {c.volumeAccess.title}
          </h2>
          <p className="mt-4 leading-relaxed text-neutral-700">
            {c.volumeAccess.p1}
          </p>
          <p className="mt-4 leading-relaxed text-neutral-700">
            {c.volumeAccess.p2}
          </p>
        </div>
      </section>

      {/* RACHAT D'OBJETS */}
      <section className="bg-sage/5 py-20">
        <div className="mx-auto max-w-[1000px] px-6 lg:px-10">
          <div className="rounded-[2rem] border border-sage/25 bg-white p-8 text-center">
            <div className="mx-auto inline-flex items-center gap-2 rounded-full border border-sage/30 bg-sage/10 px-3 py-1.5 text-xs font-bold uppercase tracking-[0.12em] text-sage">
              <GiftIcon className="h-4 w-4" />
              {c.rachatSection.badge}
            </div>
            <h2 className="mt-4 font-serif text-2xl font-extrabold text-brand-900 md:text-3xl">
              {c.rachatSection.title}
            </h2>
            <p className="mx-auto mt-4 max-w-2xl leading-relaxed text-neutral-700">
              {c.rachatSection.text}
            </p>
            <div className="mt-6">
              <Link
                href="/rachat-objets"
                className="inline-flex items-center gap-2 font-bold text-sage hover:text-ink"
              >
                {c.rachatSection.link}
                <ArrowRightIcon className="h-4 w-4" />
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* TYPE DE BIEN */}
      <section className="bg-white py-20">
        <div className="mx-auto max-w-[1000px] px-6 lg:px-10">
          <h2 className="font-serif text-2xl font-extrabold text-brand-900 md:text-3xl">
            {c.propertyTypeSection.title}
          </h2>
          <p className="mt-4 max-w-2xl leading-relaxed text-neutral-700">
            {c.propertyTypeSection.text}
          </p>
          <div className="mt-8 grid grid-cols-1 gap-4 sm:grid-cols-3">
            {c.propertyTypes.map((type, i) => {
              const Icon = PROPERTY_TYPE_ICONS[i];
              return (
                <Link
                  key={type.title}
                  href={PROPERTY_TYPE_HREFS[i]}
                  className="group flex flex-col rounded-2xl border border-sand-line bg-sand p-6 transition hover:border-accent hover:bg-white"
                >
                  <Icon className="h-7 w-7 text-accent" />
                  <p className="mt-3 font-bold text-ink">{type.title}</p>
                  <p className="mt-2 flex-1 text-sm leading-relaxed text-neutral-600">{type.text}</p>
                  <span className="mt-4 inline-flex items-center gap-1 text-sm font-bold text-accent">
                    {type.label}
                    <ArrowRightIcon className="h-4 w-4 transition group-hover:translate-x-1" />
                  </span>
                </Link>
              );
            })}
          </div>
          <p className="mt-6 text-sm text-neutral-500">
            {c.propertyTypeSection.outroBefore}
            <Link href="/vide-maison" className="font-semibold text-accent underline underline-offset-4">
              {c.propertyTypeSection.outroLink}
            </Link>
            {c.propertyTypeSection.outroAfter}
          </p>
        </div>
      </section>

      {/* URGENCE / ORGANISATION */}
      <section className="bg-sand py-20">
        <div className="mx-auto max-w-[1000px] px-6 lg:px-10">
          <h2 className="font-serif text-2xl font-extrabold text-brand-900 md:text-3xl">
            {c.urgenceSection.title}
          </h2>
          <p className="mt-4 leading-relaxed text-neutral-700">
            {c.urgenceSection.p1}
          </p>
          <p className="mt-4 leading-relaxed text-neutral-700">
            {c.urgenceSection.p2Before}
            <Link
              href="/blog/vide-maison-succession-bruxelles"
              className="font-semibold text-accent underline underline-offset-4"
            >
              {c.urgenceSection.p2Link}
            </Link>
            {c.urgenceSection.p2After}
          </p>
        </div>
      </section>

      {/* PROCESSUS D'ESTIMATION */}
      <section className="bg-white py-20">
        <div className="mx-auto max-w-[900px] px-6 lg:px-10">
          <h2 className="font-serif text-2xl font-extrabold text-brand-900 md:text-3xl">
            {c.processusSection.title}
          </h2>
          <p className="mt-4 leading-relaxed text-neutral-700">
            {c.processusSection.p1}
          </p>
          <p className="mt-4 leading-relaxed text-neutral-700">
            {c.processusSection.p2Before}
            <Link href="/realisations" className="font-semibold text-accent underline underline-offset-4">
              {c.processusSection.p2Link1}
            </Link>
            {c.processusSection.p2Mid}
            <Link href="/avis" className="font-semibold text-accent underline underline-offset-4">
              {c.processusSection.p2Link2}
            </Link>
            {c.processusSection.p2After}
          </p>
        </div>
      </section>

      {/* DECHETS / EVACUATION */}
      <section className="bg-sand py-20">
        <div className="mx-auto max-w-[900px] px-6 lg:px-10">
          <h2 className="font-serif text-2xl font-extrabold text-brand-900 md:text-3xl">
            {c.dechetsSection.title}
          </h2>
          <p className="mt-4 leading-relaxed text-neutral-700">
            {c.dechetsSection.p1}
          </p>
          <p className="mt-4 leading-relaxed text-neutral-700">
            {c.dechetsSection.p2}
          </p>
        </div>
      </section>

      {/* PREPARATION */}
      <section className="bg-white py-20">
        <div className="mx-auto max-w-[900px] px-6 lg:px-10">
          <h2 className="font-serif text-2xl font-extrabold text-brand-900 md:text-3xl">
            {c.preparationSection.title}
          </h2>
          <p className="mt-4 leading-relaxed text-neutral-700">
            {c.preparationSection.p1}
          </p>
          <p className="mt-4 leading-relaxed text-neutral-700">
            {c.preparationSection.p2Before}
            <Link
              href="/blog/checklist-avant-vide-maison"
              className="font-semibold text-accent underline underline-offset-4"
            >
              {c.preparationSection.p2Link}
            </Link>
            {c.preparationSection.p2After}
          </p>
        </div>
      </section>

      {/* TRANSPARENCE */}
      <section className="bg-sand py-20">
        <div className="mx-auto max-w-[900px] px-6 lg:px-10">
          <h2 className="font-serif text-2xl font-extrabold text-brand-900 md:text-3xl">
            {c.transparenceSection.title}
          </h2>
          <p className="mt-4 leading-relaxed text-neutral-700">
            {c.transparenceSection.p1}
          </p>
          <p className="mt-4 leading-relaxed text-neutral-700">
            {c.transparenceSection.p2}
          </p>
        </div>
      </section>

      {/* FAQ */}
      <section className="bg-white py-20">
        <div className="mx-auto max-w-[1000px] px-6 lg:px-10">
          <h2 className="font-serif text-2xl font-extrabold text-brand-900 md:text-3xl">
            {c.faqSectionTitle}
          </h2>
          <div className="mt-8 space-y-4">
            {c.faq.map((item) => (
              <div key={item.q} className="rounded-2xl border border-sand-line bg-white p-5">
                <p className="font-bold text-ink">{item.q}</p>
                <p className="mt-2 text-sm leading-relaxed text-neutral-600">{item.a}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA FINAL */}
      <section className="bg-sand py-16">
        <div className="mx-auto max-w-[800px] px-6 text-center lg:px-10">
          <h2 className="font-serif text-2xl font-extrabold text-brand-900 md:text-3xl">
            {c.ctaFinal.title}
          </h2>
          <p className="mx-auto mt-4 max-w-2xl leading-relaxed text-neutral-700">
            {c.ctaFinal.text}
          </p>
          <div className="mt-8 flex flex-wrap justify-center gap-4">
            <CtaLink href={`tel:${PHONE_TEL}`} variant="accent" icon={PhoneIcon}>
              {PHONE_DISPLAY}
            </CtaLink>
            <CtaLink href="#contactus" variant="sand" icon={ClipboardDocumentCheckIcon}>
              {c.ctaQuote}
            </CtaLink>
          </div>
        </div>
      </section>

      <Contact />
    </div>
  );
}
